import { useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { XCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLogout } from "@workspace/api-client-react";
import { useDemoProfile } from "@/context/DemoProfileContext";

const CANCEL_REASONS = [
  { value: "exploring", label: "Just exploring — not ready yet" },
  { value: "missing_features", label: "Missing features I need" },
  { value: "pricing", label: "Pricing doesn't fit my budget" },
  { value: "found_alternative", label: "Found an alternative solution" },
  { value: "ready_signup", label: "I'm ready to sign up for real" },
  { value: "technical_issues", label: "Experienced technical issues" },
  { value: "other", label: "Other" },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CancelDemoModal({ open, onClose }: Props) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const logoutMutation = useLogout();
  const { clearProfile } = useDemoProfile();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();

  if (!open) return null;

  const canConfirm = reason !== "";

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setLoading(true);
    try {
      clearProfile();
      await logoutMutation.mutateAsync();
      qc.clear();
      setLocation("/");
    } catch {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-2xl border border-border w-full max-w-md p-6 space-y-5">

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-950 flex items-center justify-center shrink-0 mt-0.5">
            <XCircle className="w-5 h-5 text-rose-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold">End Demo Session?</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              All unsaved demo data will be lost. You can start a new demo anytime.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold">
            Reason for leaving <span className="text-rose-500">*</span>
          </label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className={`rounded-xl ${!reason ? "text-muted-foreground" : ""}`}>
              <SelectValue placeholder="Select a reason…" />
            </SelectTrigger>
            <SelectContent>
              {CANCEL_REASONS.map(r => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!reason && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-3 h-3 shrink-0" />
              Please select a reason to continue
            </p>
          )}
        </div>

        <div className="flex gap-3 pt-1">
          <Button
            variant="outline"
            className="flex-1 rounded-xl"
            onClick={onClose}
            disabled={loading}
          >
            Keep Exploring
          </Button>
          <Button
            variant="destructive"
            className="flex-1 rounded-xl"
            onClick={handleConfirm}
            disabled={!canConfirm || loading}
          >
            {loading ? "Ending…" : "End Demo"}
          </Button>
        </div>
      </div>
    </div>
  );
}
